import { useCallback } from "react";
import { useGenerateSwimmersReport } from "../services/reportService";
import { useGetSwimmersReportData } from "../../swimmers/services/swimmerService";
import { exportTableToPdf } from "../../../shared/services/export/pdf/pdfExportService";
import { buildReportFileName } from "../utils/reportFilenames";
import { SWIMMERS_PDF_COLUMNS, SWIMMERS_PDF_OPTIONS } from "../config/swimmersPdfReport";
import type { SwimmerReportDataItem } from "../../../api/client/types/swimmer.types";

/**
 * Encapsulates the full generation logic for the swimmers report.
 *
 * - `format === "pdf"`:  fetches all rows from the JSON endpoint, generates
 *   the PDF client-side via jsPDF, and returns `null` (no blob to download).
 * - `format === "excel"`: delegates to the backend report endpoint and
 *   returns the `Blob` for the caller to trigger a file download.
 *
 * @returns A stable async callback matching the `onGenerate` signature
 *          expected by `ReportGenerator`.
 */
export function useSwimmersReportHandler() {
  const generateExcel = useGenerateSwimmersReport();
  const fetchReportData = useGetSwimmersReportData();

  return useCallback(
    async (
      start_date: string,
      end_date: string,
      building_id?: string,
      format?: "excel" | "pdf",
      building_name?: string,
    ): Promise<Blob | null> => {
      if (format === "pdf") {
        const { items } = await fetchReportData.mutateAsync({
          start_date,
          end_date,
          building_id,
        });

        await exportTableToPdf<SwimmerReportDataItem>({
          ...SWIMMERS_PDF_OPTIONS,
          columns: SWIMMERS_PDF_COLUMNS,
          rows: items,
          fileName: buildReportFileName({
            reportType: "swimmers",
            buildingName: building_name ?? "Todas las unidades residenciales",
            startDate: start_date,
            endDate: end_date,
            extension: "pdf",
          }),
          onError: (err) => console.error("Error generating swimmers PDF:", err),
        });

        return null;
      }

      return generateExcel.mutateAsync({ start_date, end_date, building_id, format });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
